
import { useState } from "react";
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { SubscriptionPlansTable } from "@/components/admin/subscriptions/SubscriptionPlansTable"
import { UserSubscriptionsTable } from "@/components/admin/subscriptions/UserSubscriptionsTable"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useSubscriptions } from "@/hooks/useSubscriptions"
import { toast } from "sonner"
import { UserSubscription } from "@/lib/types"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Info } from "lucide-react"
import { useSystemSettings } from "@/hooks/useSystemSettings"
import { useEffect, useState as useStateHook } from "react"

export function SubscriptionsManager() {
  const [activeTab, setActiveTab] = useState("plans")
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useStateHook<boolean>(true)
  const navigate = useNavigate()
  const { getPublicBooleanSetting } = useSystemSettings()
  
  const {
    useSubscriptionPlansQuery,
    useAllUserSubscriptionsQuery,
    useDeleteSubscriptionPlanMutation,
    useAssignUserSubscriptionMutation
  } = useSubscriptions()
  
  const { data: plans = [], isLoading: plansLoading, refetch: refetchPlans } = useSubscriptionPlansQuery()
  const { data: subscriptions = [], isLoading: subscriptionsLoading } = useAllUserSubscriptionsQuery()
  
  const deletePlan = useDeleteSubscriptionPlanMutation()
  const assignSubscription = useAssignUserSubscriptionMutation()
  
  // Check if subscriptions are enabled globally
  useEffect(() => {
    const checkSubscriptionsEnabled = async () => {
      try {
        const enabled = await getPublicBooleanSetting('subscriptions_enabled', true)
        setSubscriptionsEnabled(enabled)
      } catch (error) {
        console.error("Error checking if subscriptions are enabled:", error)
        setSubscriptionsEnabled(true)
      }
    }
    
    checkSubscriptionsEnabled()
  }, [getPublicBooleanSetting])
  
  const handleCreatePlan = () => {
    navigate('/admin/subscription-plans/new')
  }
  
  const handleDeletePlan = async (planId: string) => {
    try {
      await deletePlan.mutateAsync(planId)
      toast.success("Subscription plan deleted successfully")
      refetchPlans()
    } catch (error) {
      console.error("Failed to delete plan:", error)
      toast.error("Failed to delete subscription plan. Please try again.")
    }
  }
  
  const handleAssignSubscription = async (subscriptionData: any) => {
    try {
      await assignSubscription.mutateAsync(subscriptionData)
      toast.success("Subscription assigned successfully")
    } catch (error) {
      console.error("Failed to assign subscription:", error)
      toast.error("Failed to assign subscription. Please try again.")
    }
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Subscription Management</h2>
      </div>
      
      {!subscriptionsEnabled && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Subscriptions are currently disabled globally. Users won't see subscription features, but you can still manage plans and assignments here.
          </AlertDescription>
        </Alert>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="users">User Subscriptions</TabsTrigger>
        </TabsList>
        
        <TabsContent value="plans" className="mt-4">
          <SubscriptionPlansTable 
            plans={plans}
            onDeletePlan={handleDeletePlan}
            isLoading={plansLoading}
          />
        </TabsContent>
        
        <TabsContent value="users" className="mt-4">
          <UserSubscriptionsTable 
            subscriptions={subscriptions as unknown as UserSubscription[]}
            plans={plans}
            onAssignSubscription={handleAssignSubscription}
            isLoading={subscriptionsLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
